import { useCallback, useEffect, useMemo } from 'react';
import { CoveoAnalyticsClient, ViewEventRequest } from 'coveo.analytics/modules';

import { getEmailFromAccount, getWebStoreApiClient, isLoggedIn } from '@gn-audio/jss-core';

import { UrlMappings } from '../constants/urlMappings';
import { CoveoSearchContentType } from '../enums/coveo-search-type.enum';

export const useCoveoAnalytics = (accessToken: string, analyticsEndpoint: string) => {
  const userAnalyticsClient = useMemo(
    () =>
      new CoveoAnalyticsClient({
        token: accessToken,
        endpoint: analyticsEndpoint,
      }),
    [accessToken, analyticsEndpoint]
  );

  const getAuthorizedUserEmail = useCallback(async () => {
    const isUserLoggedIn = await isLoggedIn();
    if (!isUserLoggedIn) return '';

    const account = getWebStoreApiClient()?.account;
    return account ? getEmailFromAccount(account) : '';
  }, []);

  const getContentType = useCallback(() => {
    if (typeof window === 'undefined') return CoveoSearchContentType.Unknown;

    const currentURL = window.location.href;
    return Array.from(UrlMappings).reduce((acc, [urlFragment, contentType]) => {
      return currentURL.includes(urlFragment) ? contentType : acc;
    }, CoveoSearchContentType.Unknown);
  }, []);

  const getClickableUri = useCallback((contentType: CoveoSearchContentType): string => {
    const currentURL = window.location.href;
    const commonContentTypes = new Set([
      CoveoSearchContentType.JabraGlossary,
      CoveoSearchContentType.HearingProtection,
      CoveoSearchContentType.TechnicalWhitepapers,
      CoveoSearchContentType.WarrantyTerms,
      CoveoSearchContentType.ReleaseNotes,
      CoveoSearchContentType.ProductSupportPage,
      CoveoSearchContentType.ProductCatalog,
      CoveoSearchContentType.FAQ,
      CoveoSearchContentType.Video,
    ]);

    return commonContentTypes.has(contentType) ? currentURL.replace(/##.*/, '') : '';
  }, []);

  const sendUserViewEvent = useCallback(async () => {
    const contentType = getContentType();
    if (contentType === CoveoSearchContentType.Unknown || !userAnalyticsClient) return;

    const clickableUrl = getClickableUri(contentType);
    const viewEvent: ViewEventRequest = {
      contentIdKey: '@clickableuri',
      contentType: contentType,
      contentIdValue: clickableUrl,
      location: clickableUrl,
    };

    const authorizedUserEmail = await getAuthorizedUserEmail();
    if (authorizedUserEmail) viewEvent.customData = { context_user_email: authorizedUserEmail };

    userAnalyticsClient.sendViewEvent(viewEvent);
  }, [getAuthorizedUserEmail, getClickableUri, getContentType, userAnalyticsClient]);

  useEffect(() => {
    sendUserViewEvent();
  }, [sendUserViewEvent]);
};
